import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912) — nulls-last, numeric-vs-string and the
// empty-string rule live in one place rather than being re-derived per table.
import {sortRecords} from '@flexinet/ui-components';

import {
    clearKeyword,
    clearSelection,
    setKeyword,
} from '../reducers';

import {
    selectionSelector,
    visibilityStateSelector,
} from '../selectors';

import {
    useAppDispatch,
    useAppSelector
} from '../../../hooks/useTypedReactRedux';

import {
    useDeleteTopLevelDomainMutation,
    useTopLevelDomainsInfiniteQuery
} from './useTopLevelDomainsQuery';
import {TopLevelDomain} from '../types';

// ⚠️ `created` DESCENDING — newest first, the ordinary console default, on the
// one key here that is both immutable and set at write time. Never `updated`:
// it moves under the reader.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<TopLevelDomain> = {
    columnAccessor: 'created',
    direction: 'desc',
};

/** Which fields the keyword box searches. Exported for its own test. */
export const matchesKeyword = (item: TopLevelDomain, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [item.name, item.tldType, item.tldManager];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

// FNP-944: records come from React Query; selection and keyword stay in Redux.
// FNP-1292: paging is now TOKEN paging, so the Redux `visibility` slice no
// longer drives the list. `page`/`limit`/`totalPages`/`visibleCount` are gone
// from the return shape — under a token model there is no page to jump to, no
// total to divide and no page size for a user to choose.
export const useTopLevelDomains = () => {

    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));
    const visibility = useAppSelector((state) => visibilityStateSelector(state));

    const query = useTopLevelDomainsInfiniteQuery();

    const deleteMutation = useDeleteTopLevelDomainMutation();

    // Component state, not the module store: sort order is how ONE operator is
    // looking at the table right now, unlike the keyword, which is shared with
    // sibling components through the slice.
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<TopLevelDomain>>(DEFAULT_SORT_STATUS);

    // A type guard, not a bare flatMap — a partial page object would otherwise
    // throw inside a render.
    const items: TopLevelDomain[] = (query.data?.pages ?? []).flatMap(
        page => (page && 'items' in page) ? (page.items ?? []) : [],
    ).filter(Boolean);

    const keyword = visibility.keyword ?? '';

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ THE SORT AND THE KEYWORD ARE CLIENT-SIDE, over the pages fetched so
    // far. That is a property of token pagination, not an oversight: sorting
    // page 1 does not sort the dataset, and the footer therefore reports a
    // LOADED count rather than a total it cannot know.
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle)),
        sortStatus,
    );

    const fetchRecords = () => {
        query.refetch();
    };

    const deleteRecords = () => {
        if (Object.keys(selection).length > 0) {
            for (const key in selection) {
                deleteMutation.mutate(key);
            }
            // Clear the checkboxes once the deletions are issued — otherwise the
            // selection keeps naming records that no longer exist.
            dispatch(clearSelection());
        }
    };

    const handleFilterByKeyword = (keyword: string) => {
        if (keyword !== '') {
            dispatch(setKeyword(keyword));
        } else {
            dispatch(clearKeyword());
        }
    };

    const handleBatchActionCancel = () => {
        dispatch(clearSelection());
    };

    return {
        // The loaded set BEFORE the keyword narrows it — the window the
        // footer's count is measured against.
        items,
        visibleItems,
        keyword,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        // ⚠️ Reachable only because the service re-throws now. While it
        // swallowed and returned undefined, this was permanently false and a
        // failed load rendered as an empty table.
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        isReloading: query.isRefetching,
        // ⚠️ DERIVED FROM hasNextPage, NEVER FROM items.length.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        fetchRecords,
        deleteRecords,
        handleFilterByKeyword,
        handleBatchActionCancel,
    };
};
